/**
 * ============================================================================
 * Fast Bundle Cart Sync Fix (v7.0)
 * ============================================================================
 *
 * Loaded by the theme via snippets/cart-drawer.liquid on every page.
 * Previously lived in Fast Bundle app settings (Custom CSS and JS), but
 * Fast Bundle only injects custom JS on pages with bundle widgets — not on
 * homepages, collection pages, or individual product pages where customers
 * also use the cart drawer.
 *
 * WHAT IT DOES:
 *   Fixes stale pricing in the cart drawer AND on the /cart page caused by a
 *   timing conflict between Fast Bundle's discount registration and the
 *   Minimog theme's section rendering. Fast Bundle's Buy More Save More
 *   discount is a Shopify Functions app discount whose per-cart config lives
 *   on Fast Bundle's servers; after every cart change their script must
 *   round-trip (PATCH api.fastbundle.co/v1/carts/discounts/) before Shopify
 *   renders the new tier. The theme re-renders instantly, so without this
 *   script the cart shows pre-discount prices until a full page reload.
 *
 * HOW IT WORKS:
 *   Patches three Minimog theme methods to schedule a delayed re-render after
 *   Fast Bundle finishes recalculating:
 *
 *   Patch 1 — MCartDrawer.renderContents (add-to-cart)
 *     When a product is added, the theme opens the drawer with pre-discount
 *     HTML. This patch waits for detectBundleDiscounts(), then re-fetches
 *     the drawer sections with corrected pricing.
 *
 *   Patch 2 — MCartDrawerItems.updateQuantity (drawer qty +/- and removal)
 *     When quantity is changed or an item is removed via the cart drawer, the
 *     theme calls /cart/change.js and updates the DOM immediately. This patch
 *     triggers the same re-fetch afterward so prices reflect the new tier.
 *
 *   Patch 3 — m-cart (MCartTemplate) updateQuantity (/cart PAGE qty +/- and
 *     removal, including the "Remove" link, which routes through the same
 *     method via m-cart-remove-button). Same treatment as Patch 2 but
 *     re-fetches the cart-template section. NEW in v7.0 — previously the
 *     /cart page never self-corrected and needed a manual reload.
 *
 * DEPENDS ON:
 *   - Minimog theme cart elements (MCartDrawer #MinimogCartDrawer,
 *     m-cart-drawer-items, and m-cart #MinimogCart custom elements)
 *   - Fast Bundle's detectBundleDiscounts() global function (gracefully skipped
 *     if not available on the current page)
 *   - Shopify Section Rendering API
 *
 * RELATED THEME FILES:
 *   - assets/cart-drawer.js — MCartDrawer and MCartDrawerItems classes
 *   - assets/cart.js — MCartTemplate base class (updateQuantity method;
 *     registered as <m-cart id="MinimogCart"> by sections/cart-template.liquid)
 *   - snippets/bundle-progress.liquid — Buy More Save More progress bar
 *   - snippets/cart-drawer.liquid — loads this script; switching logic between
 *     BMS and free-gift bars
 *
 * LAST UPDATED: 2026-08-10
 *
 * CHANGELOG:
 *   v5 (2026-03-31) — Initial dual-patch for renderContents + updateQuantity
 *   v6 (2026-04-01) — Added button disable lock during sync to prevent race
 *     conditions from rapid clicking.
 *   v6.1 (2026-04-01) — Increased Patch 2 delay from 1000ms to 2500ms. Moved
 *     from Fast Bundle app settings into theme assets for global page coverage.
 *     Added console diagnostics for troubleshooting.
 *   v6.2 (2026-04-01) — Reduced Patch 2 delay from 2500ms to 1000ms based on
 *     console timing data showing detectBundleDiscounts completes in ~260ms.
 *     Total click-to-correct drops from ~3.8s to ~2.3s.
 *   v6.3 (2026-04-01) — Controls now locked via CSS class on drawer element
 *     (survives innerHTML swaps). Buttons disabled immediately on qty change,
 *     not after the 1000ms delay. Closes the clickable gap at step 3.
 *   v6.4 (2026-07-03) — Hardened the section re-fetch: requests sections
 *     relative to the CURRENT page path instead of the root URL (a root
 *     request can be hijacked by A/B-testing apps like Shoplift and answered
 *     with a full HTML page), skips the fetch when the section list is empty,
 *     and verifies the response is JSON before parsing. Fixes the
 *     "Unexpected token '<'" SyntaxError seen after add-to-cart.
 *   v6.5 (2026-07-03) — Isolated the detectBundleDiscounts() call in its own
 *     try/catch: the SyntaxError actually originates INSIDE Fast Bundle's
 *     function (their fetch receives HTML), and letting it propagate aborted
 *     our whole section repaint. Now their failure is logged and the repaint
 *     proceeds anyway. Their own duplicate uncaught rejection can only be
 *     fixed by Fast Bundle support.
 *   v7.0 (2026-08-10) — Extended sync to the /cart page: new Patch 3 wraps
 *     the m-cart element's updateQuantity (covers qty +/-, the Remove link,
 *     and remove-free-gift.js, which all route through it) and re-fetches the
 *     cart-template section so cart-page prices self-correct ~2-3.5s after a
 *     change instead of only on reload. Section swaps are now SCOPED to the
 *     host container id (getElementById first, document-wide querySelector
 *     only as fallback) — fixes a latent collision on /cart where the drawer
 *     and the page share identical data-attribute selectors, so an unscoped
 *     swap could write drawer HTML into the page (or vice versa). Control
 *     lock CSS extended to #MinimogCart.
 *
 * KNOWN ISSUE:
 *   When crossing tier boundaries via +/- (e.g., 4→5 shirts), the drawer may
 *   briefly show a split line item (1 at full price + N at old tier) because
 *   detectBundleDiscounts() mutates the cart mid-render. The server-side cart
 *   is correct — a page refresh resolves the display.
 * ============================================================================
 */
(function() {

  var FB_SYNC_VERSION = 'v7.0';
  console.log('[FB-Sync ' + FB_SYNC_VERSION + '] loaded');

  /**
   * Lock UI controls during sync to prevent double-click race conditions.
   * Disables +/- buttons, remove buttons, and quantity inputs in the cart
   * drawer and on the /cart page.
   */
  // Inject a CSS rule that locks controls via a class on the host element.
  // This survives innerHTML swaps (unlike per-element disabled attributes).
  var style = document.createElement('style');
  style.textContent =
    '#MinimogCartDrawer.fb-sync-locked .m-quantity__button,' +
    '#MinimogCartDrawer.fb-sync-locked m-cart-remove-button,' +
    '#MinimogCartDrawer.fb-sync-locked .m-quantity__input,' +
    '#MinimogCart.fb-sync-locked .m-quantity__button,' +
    '#MinimogCart.fb-sync-locked m-cart-remove-button,' +
    '#MinimogCart.fb-sync-locked .m-quantity__input {' +
    '  pointer-events: none !important;' +
    '  opacity: 0.5 !important;' +
    '}';
  document.head.appendChild(style);

  function setCartControlsEnabled(enabled) {
    ['MinimogCartDrawer', 'MinimogCart'].forEach(function(id) {
      var el = document.getElementById(id);
      if (!el) return;
      if (enabled) {
        el.classList.remove('fb-sync-locked');
      } else {
        el.classList.add('fb-sync-locked');
      }
    });
  }

  /**
   * Shared helper: wait for Fast Bundle to recalculate, then re-fetch
   * and swap in corrected section HTML for the host's cart sections.
   * `host` is any element exposing getSectionsToRender() — the drawer
   * (MCartDrawer) for Patches 1-2, the m-cart element for Patch 3.
   */
  async function refetchCartSections(host, trigger) {
    var t0 = performance.now();
    console.log('[FB-Sync ' + FB_SYNC_VERSION + '] re-fetch START (trigger: ' + (trigger || 'unknown') + ')');
    try {
      // Disable cart controls to prevent race conditions from rapid clicks
      setCartControlsEnabled(false);

      // Wait for Fast Bundle to detect and apply bundle discounts
      // (gracefully skipped on pages where Fast Bundle hasn't loaded)
      if (typeof detectBundleDiscounts === 'function') {
        console.log('[FB-Sync] calling detectBundleDiscounts...');
        try {
          await detectBundleDiscounts();
          console.log('[FB-Sync] detectBundleDiscounts done (' + Math.round(performance.now() - t0) + 'ms)');
        } catch (fbErr) {
          // Fast Bundle's own function failed (e.g. its fetch got HTML instead
          // of JSON). Not fatal for us - continue with the section repaint so
          // the cart still refreshes.
          console.log('[FB-Sync] detectBundleDiscounts failed (' + fbErr + '), continuing with repaint');
        }
      } else {
        console.log('[FB-Sync] detectBundleDiscounts not available on this page, skipping');
      }

      // Small buffer to ensure Shopify has processed the discount update
      await new Promise(function(resolve) { setTimeout(resolve, 300); });

      // Build the list of sections to re-fetch from the host's own config
      var sectionEntries = host.getSectionsToRender();
      var sectionNames = sectionEntries.map(function(s) {
        return s.section || s.id;
      });
      var uniqueSections = sectionNames.filter(function(val, idx, arr) {
        return val && arr.indexOf(val) === idx;
      });

      // Nothing to re-fetch (e.g. host config unavailable) - bail cleanly
      if (uniqueSections.length === 0) {
        console.log('[FB-Sync] no sections to re-fetch, skipping');
        setCartControlsEnabled(true);
        return;
      }

      // Re-fetch sections with updated bundle pricing. Use the CURRENT page
      // path, not the root URL: root requests can be intercepted by
      // A/B-testing apps (Shoplift) and answered with a full HTML page.
      var sectionsUrl = new URL(window.location.pathname, window.location.origin);
      sectionsUrl.searchParams.set('sections', uniqueSections.join(','));
      var resp = await fetch(sectionsUrl.toString());
      if (!resp.ok) {
        setCartControlsEnabled(true);
        return;
      }

      // Guard against HTML responses (redirects, interstitials) - parsing
      // them as JSON is what threw the "Unexpected token '<'" SyntaxError
      var contentType = resp.headers.get('content-type') || '';
      if (contentType.indexOf('application/json') === -1) {
        console.log('[FB-Sync] section re-fetch returned non-JSON (' + contentType + '), skipping swap');
        setCartControlsEnabled(true);
        return;
      }
      var data = await resp.json();

      // Swap in the corrected HTML for each section target.
      // v7.0: scope selector lookups to the host container id first — the
      // drawer and the /cart page use IDENTICAL data-attribute selectors
      // ([data-minimog-cart-items], [data-cart-subtotal], ...), so on /cart an
      // unscoped document.querySelector can grab the wrong surface's node.
      sectionEntries.forEach(function(s) {
        var sectionKey = s.section || s.id;
        var sectionHtml = data[sectionKey];
        if (!sectionHtml) return;

        var root = s.id ? document.getElementById(s.id) : null;
        var target;
        if (s.selector) {
          // If the host container exists, only look inside it. Fall back to a
          // document-wide search only when the container id isn't in the DOM
          // (preserves pre-v7.0 behavior on pages without the container).
          target = root ? root.querySelector(s.selector) : document.querySelector(s.selector);
        } else {
          target = root;
        }
        if (!target) return;

        var parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        var sourceRoot = s.id ? parsed.getElementById(s.id) : null;
        var source;
        if (s.selector) {
          source = sourceRoot ? sourceRoot.querySelector(s.selector) : parsed.querySelector(s.selector);
        } else {
          source = sourceRoot;
        }

        if (source && target) {
          target.innerHTML = source.innerHTML;
        }
      });

      // Log cart state after re-fetch for diagnostics
      try {
        var cartCheck = await fetch(window.Shopify.routes.root + 'cart.json');
        var cartData = await cartCheck.json();
        console.log('[FB-Sync] section re-fetch done (' + Math.round(performance.now() - t0) + 'ms). Cart state: ' +
          cartData.item_count + ' items, $' + (cartData.total_price / 100).toFixed(2) +
          ', lines: ' + cartData.items.map(function(i) { return i.quantity + 'x$' + (i.final_price / 100).toFixed(2); }).join(' + '));
      } catch(e) {}

      // Re-initialize cart drawer UI (quantity buttons, cart note, etc.)
      // The m-cart page element has no such hook (its events are delegated),
      // so this typeof guard simply skips it for Patch 3.
      if (typeof host.onCartDrawerUpdate === 'function') {
        host.onCartDrawerUpdate();
      }

      // Re-enable cart controls after re-render is complete
      setCartControlsEnabled(true);
      console.log('[FB-Sync ' + FB_SYNC_VERSION + '] re-fetch COMPLETE (' + Math.round(performance.now() - t0) + 'ms)');
    } catch (err) {
      console.log('[FB-Sync ' + FB_SYNC_VERSION + '] ERROR:', err);
      // Always re-enable controls, even on error
      setCartControlsEnabled(true);
    }
  }

  function init() {
    var drawer = document.getElementById('MinimogCartDrawer');
    if (!drawer || typeof drawer.renderContents !== 'function') {
      setTimeout(init, 200);
      return;
    }

    // Only patch once (prevents double-patching if Fast Bundle custom JS also loads)
    if (drawer.__fbFixPatched) return;
    drawer.__fbFixPatched = true;
    console.log('[FB-Sync ' + FB_SYNC_VERSION + '] patches applied to MCartDrawer + MCartDrawerItems');

    // --- Patch 1: renderContents (add-to-cart) ---
    var origRenderContents = drawer.renderContents.bind(drawer);

    drawer.renderContents = function(t) {
      // Let the theme do its normal render first (opens the drawer)
      origRenderContents(t);

      // Then schedule a corrected re-render after bundle detection
      console.log('[FB-Sync] Patch 1: add-to-cart, scheduling re-fetch in 500ms');
      setTimeout(function() {
        refetchCartSections(drawer, 'add-to-cart');
      }, 500);
    };

    // --- Patch 2: updateQuantity on MCartDrawerItems (quantity +/- and removal) ---
    var drawerItems = document.querySelector('m-cart-drawer-items');
    if (drawerItems && typeof drawerItems.updateQuantity === 'function') {
      var origUpdateQuantity = drawerItems.updateQuantity.bind(drawerItems);

      drawerItems.updateQuantity = function(line, quantity, name) {
        // Disable controls immediately to prevent clicks during the gap
        // between the theme's re-render and our sync re-render
        setCartControlsEnabled(false);

        // Let the theme do its normal /cart/change.js call and DOM update
        origUpdateQuantity(line, quantity, name);

        // Then schedule a corrected re-render after bundle detection
        // 1000ms delay — detectBundleDiscounts() typically completes in ~260ms,
        // but we need the theme's /cart/change.js fetch to finish first
        console.log('[FB-Sync] Patch 2: qty change (line ' + line + ' → ' + quantity + '), scheduling re-fetch in 1000ms');
        setTimeout(function() {
          refetchCartSections(drawer, 'qty-change');
        }, 1000);
      };
    }
  }

  // --- Patch 3: updateQuantity on m-cart (/cart PAGE quantity +/- and removal) ---
  // Separate init with its own retry: #MinimogCart only exists on the /cart
  // template, and the custom element may upgrade after this script runs.
  // On other pages the retries find nothing and stop quietly.
  var cartPageInitTries = 0;
  function initCartPage() {
    var cartPage = document.getElementById('MinimogCart');
    if (!cartPage || typeof cartPage.updateQuantity !== 'function') {
      if (cartPageInitTries++ < 25) setTimeout(initCartPage, 200);
      return;
    }

    if (cartPage.__fbFixPatched) return;
    cartPage.__fbFixPatched = true;
    console.log('[FB-Sync ' + FB_SYNC_VERSION + '] Patch 3 applied to m-cart (/cart page)');

    var origCartUpdateQuantity = cartPage.updateQuantity.bind(cartPage);

    cartPage.updateQuantity = function(line, quantity, name) {
      // Disable controls immediately to prevent clicks during the gap
      setCartControlsEnabled(false);

      // Let the theme do its normal /cart/change.js call and DOM update
      origCartUpdateQuantity(line, quantity, name);

      // Then schedule a corrected re-render after bundle detection.
      // Same 1000ms rationale as Patch 2. Re-fetches the cart-template
      // section via the m-cart element's own getSectionsToRender().
      console.log('[FB-Sync] Patch 3: cart page qty change (line ' + line + ' → ' + quantity + '), scheduling re-fetch in 1000ms');
      setTimeout(function() {
        refetchCartSections(cartPage, 'cart-page-qty-change');
      }, 1000);
    };
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { init(); initCartPage(); });
  } else {
    init();
    initCartPage();
  }
})();
